/* eslint-disable spire/export-styles */
import { fetch } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { appendLegacyParam, checkSiteRestrictions } from "@WhiteCap/Common/Utillities/utils";
import { parseCommaSeparated } from "@WhiteCap/Common/Utillities/utils";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const CurrentBillToState = getCurrentBillToState(state);
    return {
        userName: session.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        userRoles: session.userRoles,
        CurrentBillToState,
        newSiteRestrictedRoles: state.context.settings?.properties?.newSiteRestrictedRoles,
        newSiteRestrictedCustomers: state.context.settings?.properties?.newSiteRestrictedCustomers,
    };
};

const mapDispatchToProps = {
    loadCurrentBillTo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/**
 * Invisible widget placed in the header (or any shared zone) that detects
 * when the user is authenticated on mount — typically after an SSO external
 * provider callback lands on OCC — and relays the session to the Next.js
 * storefront via the cross-domain token capture → exchange flow.
 *
 * This is the SSO counterpart of the CaptureAndRelayToNextJs handler in
 * SignInExtentions.ts, which handles standard username/password logins.
 * Both paths write a `crossdomain_relay_sent` flag to sessionStorage so
 * only one fires.
 */
const PostLoginRelayWidgetComponent: React.FC<Props> = ({
    userName,
    currentUserIsGuest,
    isPunchOutSession,
    userRoles,
    CurrentBillToState,
    loadCurrentBillTo,
    newSiteRestrictedRoles,
    newSiteRestrictedCustomers,
}: Props) => {
    const hasRedirected = useRef(false);

    // Load billTo data when component mounts
    useEffect(() => {
        if (userName && !currentUserIsGuest) {
            loadCurrentBillTo();
        }
    }, [userName, currentUserIsGuest, loadCurrentBillTo]);

    // Main relay logic - waits for billTo data to be loaded
    useEffect(() => {
        // Don't proceed if already redirected
        if (hasRedirected.current) {
            return;
        }

        // Parse restriction lists
        const restrictedRoles = parseCommaSeparated(newSiteRestrictedRoles);
        const restrictedCustomers = parseCommaSeparated(newSiteRestrictedCustomers);

        // Use centralized restriction logic
        const restrictionResult = checkSiteRestrictions({
            userName,
            currentUserIsGuest,
            isPunchOutSession,
            userRoles,
            customerNumber: CurrentBillToState.value?.customerNumber,
            isBillToLoading: CurrentBillToState.isLoading,
            restrictedRoles,
            restrictedCustomers,
        });

        // If should stay on legacy OR waiting for data, don't redirect
        if (restrictionResult.shouldStayOnLegacy || restrictionResult.shouldWaitForData) {
            return;
        }

        // Avoid re-triggering on subsequent page loads
        if (sessionStorage.getItem("crossdomain_relay_sent")) {
            return;
        }

        hasRedirected.current = true;

        (async () => {
            try {
                sessionStorage.setItem("crossdomain_relay_sent", "1");

                const resp = await fetch("/api/v1/whitecap/crossdomain/capture", {
                    method: "POST",
                    credentials: "same-origin",
                });

                if (!resp.ok) {
                    return;
                }

                const data = await resp.json();
                const token = data?.relayToken;
                const relayBaseUrl = data?.relayUrl;

                if (!token || !relayBaseUrl) {
                    return;
                }

                const relayUrl = `${relayBaseUrl}?token=${encodeURIComponent(token)}`;

                window.location.replace(relayUrl);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Relay capture failed", err);
            }
        })();
    }, [
        userName,
        currentUserIsGuest,
        isPunchOutSession,
        userRoles,
        CurrentBillToState.isLoading,
        CurrentBillToState.value,
        newSiteRestrictedRoles,
        newSiteRestrictedCustomers,
    ]);

    return null;
};

const PostLoginRelayWidget = connect(mapStateToProps, mapDispatchToProps)(PostLoginRelayWidgetComponent);

const widgetModule: WidgetModule = {
    component: PostLoginRelayWidget,
    definition: {
        group: "Common",
        displayName: "Post Login Relay",
        allowedContexts: ["Header"],
    },
};

export default widgetModule;
